import { useContext, useMemo } from 'react';
import { useQuery } from 'react-query';
import { UserContext } from '../..';
import { ROLE_ADMIN } from '../../constants';
import { fullName, useFilterOptions } from '../Home/useFilterOptions';
import { archivosFetch } from '../../utils/fetch';

// The step 2 lists: "Service" and "Émetteur".
//
// Same calls and same cache as the Home (useFilterOptions). The users
// list is `requireAdmin` on the backend: a USER does not get it, and for them
// "Émetteur" stays as free text, which is what the previous form
// (Step2With/WithoutQRCode) ended up offering with its "Zone libre" field.
//
// Issuer filtering, inherited from those forms:
//   - if the account has delegations ("Pour le compte de", `users`), only
//     those accounts can appear as issuer;
//   - with a chosen service, only the users of that service. Membership is
//     read off the account (`user.services`), the only place it is recorded.
//
// Next to the accounts, the issuers typed by hand on earlier documents
// (`GET /api/archives/issuers`, open to every account, limited to the
// documents the caller may read), so they can be picked again. Their value is
// the text itself, which is what typing it would store. They belong to no
// service and to no delegation, so the filters above leave them alone. A USER
// gets these alone.

export type Option = { value: string; label: string };

export const ISSUERS_KEY = 'ARCHIVE_ISSUERS';

const fetchIssuers = async (): Promise<string[]> => {
  const response = await archivosFetch(
    `${process.env.REACT_APP_API_URL}/api/archives/issuers`,
  );
  if (!response.ok) return [];

  const body = await response.json();
  return Array.isArray(body?.issuers) ? body.issuers : [];
};

export const useMetadataOptions = (serviceId: string) => {
  const userContext = useContext(UserContext);
  const me = userContext?.mainUser ?? null;
  const isAdmin = me?.role === ROLE_ADMIN;

  const { users, services } = useFilterOptions(isAdmin);

  // Refetched each time the form opens: the last document may have added one.
  const typedIssuers = useQuery<string[]>([ISSUERS_KEY], fetchIssuers, {
    retry: false,
  });

  const serviceOptions = useMemo<Option[]>(
    () =>
      services
        .filter((service) => service.active !== false)
        .map((service) => ({ value: service.id, label: service.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    [services],
  );

  const issuerOptions = useMemo<Option[]>(() => {
    const delegates = me?.users ?? [];
    const service = services.find((item) => item.id === serviceId);

    const accounts = users
      .filter((user) => Boolean(user.id))
      .filter((user) =>
        delegates.length > 0 ? delegates.includes(user.id as string) : true,
      )
      .filter((user) =>
        service ? (user.services ?? []).includes(service.id) : true,
      )
      .map((user) => ({ value: user.id as string, label: fullName(user) }));

    // A typed name that reads like one of the accounts offered is left to the
    // account: two identical labels could not be told apart in the list.
    const labels = new Set(accounts.map((option) => option.label));
    const typed = (typedIssuers.data ?? [])
      .filter((name) => !labels.has(name))
      .map((name) => ({ value: name, label: name }));

    return [...accounts, ...typed].sort((a, b) =>
      a.label.localeCompare(b.label, 'fr'),
    );
  }, [users, services, serviceId, me, typedIssuers.data]);

  // For the summary: the name of what was stored as an id.
  const serviceName = (id: string): string =>
    services.find((service) => service.id === id)?.name ?? (id || '—');

  const issuerName = (value: string): string => {
    if (!value) return '—';
    const found = users.find((user) => user.id === value);
    return found ? fullName(found) || value : value;
  };

  return { serviceOptions, issuerOptions, serviceName, issuerName };
};
